import type {
  CompleteLoginResponseData,
  CompleteLoginSuccessResponse,
  LoginFormPayload,
  LoginSuccessResponse,
  SMSLoginFormPayload,
  SMSSendPayload,
  ChangeRegistryPhonePayload,
} from '@/types/auth'
import type { DefaultError } from '@/types/httpError'
import type { UserData } from '@/types/user'
import type { RootActions, RootGetters, RootState } from './types'
import {
  loginApiCall,
  sendSMSApiCall,
  smsLoginApiCall,
  logoutApiCall,
  changeRegistryPhoneApiCall,
  adminLoginApiCall,
  adminSMSLoginApiCall,
  adminSendSMSApiCall,
  adminLogoutApiCall,
} from '@/api/auth'
import { getProfileApiCall, updateProfileApiCall, updateProfileStepApiCall } from '@/api/profile'
import { useApiCall } from '@/composables/useApiCall'
import { getStorageItemWithExpiry, setStorageItemWithExpiry } from '@/helpers/localStorage'
import { defineStore } from 'pinia'
import type { SuccessResponse } from '@/types/httpResponse'
import type { UpdateProfilePayload, UpdateProfileStepPayload } from '@/types/company'

const authToken = getStorageItemWithExpiry<string>('authToken')
const refreshToken = getStorageItemWithExpiry<string>('ref')

export const useAuthStore = defineStore<string, RootState, RootGetters, RootActions>('auth', {
  state() {
    if (!authToken) {
      localStorage.clear()
    }

    return {
      token: authToken,
      tokenType: getStorageItemWithExpiry<string>(`tokenType_${authToken}`),
      refreshToken,
      user: getStorageItemWithExpiry<UserData>(`user_${authToken}`),
      sendCodeResponse: null,
      isAdmin: getStorageItemWithExpiry<boolean>('isAdmin') ?? false,
    }
  },
  getters: {
    isAuth: (state: RootState) => !!state.token,
  },
  actions: {
    async clearStore() {
      this.token = null
      this.user = null
      localStorage.clear()
    },
    writeData(payload: CompleteLoginResponseData) {
      const { token, expiresIn, refreshExpiresIn, user, tokenType } = payload
      this.$patch({
        token: token.accessToken,
        refreshToken: token.refreshToken,
        tokenType,
        user,
      })
      setStorageItemWithExpiry('authToken', token.accessToken, expiresIn * 24 * 7 * 1000)
      setStorageItemWithExpiry(
        'ref',
        token.refreshToken,
        (refreshExpiresIn || 3600) * 24 * 7 * 1000,
      )
      setStorageItemWithExpiry(`user_${token.accessToken}`, user)
      setStorageItemWithExpiry(`tokenType_${token.accessToken}`, tokenType)
      setStorageItemWithExpiry('isAdmin', this.isAdmin)
    },
    async login(payload: LoginFormPayload) {
      const {
        data: loginData,
        executeApiCall: loginAction,
        error: loginError,
      } = useApiCall<LoginSuccessResponse, DefaultError, LoginFormPayload>(
        loginApiCall,
        true,
        payload,
      )

      try {
        await loginAction()
        if (loginData.value) {
          return loginData.value.data.message
        }
      } catch {
        throw loginError.value
      }
    },
    async completeLogin(payload: SMSLoginFormPayload) {
      const {
        data: SMSLoginData,
        executeApiCall: SMSLoginAction,
        error: SMSLoginError,
      } = useApiCall<CompleteLoginSuccessResponse, DefaultError, SMSLoginFormPayload>(
        smsLoginApiCall,
        true,
        payload,
      )

      try {
        await SMSLoginAction()
        if (SMSLoginData.value) {
          this.writeData(SMSLoginData.value.data)
        }
      } catch {
        throw SMSLoginError.value
      }
    },
    async changeRegistryPhone(payload: ChangeRegistryPhonePayload) {
      const { executeApiCall: changeRegistryPhoneAction, error: changeRegistryPhoneError }
        = useApiCall<'', DefaultError, ChangeRegistryPhonePayload>(
          changeRegistryPhoneApiCall,
          true,
          payload,
        )

      try {
        await changeRegistryPhoneAction()
      } catch {
        if (changeRegistryPhoneError.value) {
          throw changeRegistryPhoneError.value.data
        }
      }
    },
    async sendSMS(payload: SMSSendPayload) {
      const {
        data: sendSMSData,
        executeApiCall: sendSMSAction,
        error: sendSMSError,
      } = useApiCall<'', DefaultError, SMSSendPayload>(sendSMSApiCall, true, payload)

      try {
        await sendSMSAction()
        if (sendSMSData.value) {
          this.sendCodeResponse = sendSMSData.value
        }
      } catch {
        if (sendSMSError.value) {
          throw sendSMSError.value
        }
      }
    },
    async logout() {
      const { executeApiCall: logoutAction, error: logoutError } = useApiCall<'', DefaultError>(
        logoutApiCall,
        true,
      )

      try {
        await logoutAction()
        await this.clearStore()
      } catch {
        if (logoutError.value) {
          throw logoutError.value.data
        }
      }
    },
    async adminLogin(payload: LoginFormPayload) {
      const {
        data: adminLoginData,
        executeApiCall: adminLoginAction,
        error: adminLoginError,
      } = useApiCall<LoginSuccessResponse, DefaultError, LoginFormPayload>(
        adminLoginApiCall,
        true,
        payload,
      )

      try {
        await adminLoginAction()
        if (adminLoginData.value) {
          return adminLoginData.value.data.message
        }
      } catch {
        throw adminLoginError.value
      }
    },
    async adminCompleteLogin(payload: SMSLoginFormPayload) {
      const {
        data: adminSMSLoginData,
        executeApiCall: adminSMSLoginAction,
        error: adminSMSLoginError,
      } = useApiCall<CompleteLoginSuccessResponse, DefaultError, SMSLoginFormPayload>(
        adminSMSLoginApiCall,
        true,
        payload,
      )

      try {
        await adminSMSLoginAction()
        if (adminSMSLoginData.value) {
          this.isAdmin = true
          this.writeData(adminSMSLoginData.value.data)
        }
      } catch {
        throw adminSMSLoginError.value
      }
    },
    async adminSendSMS(payload: SMSSendPayload) {
      const {
        data: sensSMSData,
        executeApiCall: sendSMSAction,
        error: sendSMSError,
      } = useApiCall<'', DefaultError, SMSSendPayload>(adminSendSMSApiCall, true, payload)

      try {
        await sendSMSAction()
        if (sensSMSData.value) {
          this.sendCodeResponse = sensSMSData.value
        }
      } catch {
        if (sendSMSError.value) {
          throw sendSMSError.value
        }
      }
    },
    async adminLogout() {
      const { executeApiCall: logoutAction, error: logoutError } = useApiCall<'', DefaultError>(
        adminLogoutApiCall,
        true,
      )

      try {
        await logoutAction()
        await this.clearStore()
        this.isAdmin = false
      } catch {
        if (logoutError.value) {
          throw logoutError.value.data
        }
      }
    },
    async getProfile() {
      const {
        data: getProfileData,
        executeApiCall: getProfileAction,
        error: getProfileError,
      } = useApiCall<UserData, DefaultError>(getProfileApiCall, true)

      try {
        await getProfileAction()
        return getProfileData.value ?? null
      } catch {
        if (getProfileError.value) {
          throw getProfileError.value.data
        }
      }
      return null
    },
    async updateProfile(payload: UpdateProfilePayload) {
      const {
        data: updatedProfile,
        executeApiCall: updateProfileAction,
        error: updateProfileError,
      } = useApiCall<UserData, DefaultError, UpdateProfilePayload>(
        updateProfileApiCall,
        true,
        payload,
      )

      try {
        await updateProfileAction()
        return updatedProfile.value
      } catch {
        if (updateProfileError.value) {
          throw updateProfileError.value
        }
      }
      return null
    },
    async updateProfileStep(payload: UpdateProfileStepPayload) {
      const {
        data: updatedUser,
        executeApiCall: updateProfileAction,
        error: updateProfileError,
      } = useApiCall<SuccessResponse<UserData>, DefaultError, UpdateProfileStepPayload>(
        updateProfileStepApiCall,
        true,
        payload,
      )

      try {
        await updateProfileAction()
        if (updatedUser.value) {
          this.user = updatedUser.value.data
          setStorageItemWithExpiry(`user_${this.token}`, updatedUser.value.data)
        }
      } catch {
        if (updateProfileError.value) {
          throw updateProfileError.value
        }
      }
    },
  },
})
